import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
// import { useRouter } from "next/router";
import Link from "next/link";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS } from "@contentful/rich-text-types";
import { ComponentMap } from "@/components/index";
import { useSession } from "./providers/session";
import ProductSchema from "@/utils/product";
import MD from "react-markdown";
import { richContentOptions } from "@/components/content-options";

const CourseDates = dynamic(() => import("@/components/course-dates"));
const KlarnaDisplay = dynamic(() => import("@/components/klarna"), {
  ssr: false,
});

function com(a, b) {
  const date1 = new Date(a.sys.updatedAt).getTime();
  const date2 = new Date(b.sys.updatedAt).getTime();
  return date1 - date2;
}

const Strong = (text) => (
  <strong className="font-medium text-black">{text}</strong>
);

const Heading3 = (node, children) => {
  return (
    <h6 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">
      {children}
    </h6>
  );
};

const Heading4 = (node, children) => (
  <div className="flex items-center">
    <h4 className="shrink-0 pr-4 bg-white text-sm tracking-wider font-semibold uppercase text-brand-600">
      {children}
    </h4>
    <div className="flex-1 border-t-2 border-gray-200"></div>
  </div>
);

const UL = ({ node, children, fields }) => {
  const containsKit = fields.kitName || fields.kitPrice;
  const withLink = fields.kitLink;
  const kitText = fields.freeKit ? (
    withLink && withLink.length ? (
      <p className="p-5 text-sm">
        <span className="freePrice">FREE</span>{" "}
        <a
          href={`${fields.kitLink}`}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-brand-600 hover:text-brand-500"
        >
          {fields.kitName}{" "}
          {fields.kitPrice
            ? `(worth £${fields.kitPrice}!) included in this course`
            : ``}
        </a>
      </p>
    ) : (
      <p className="p-5 text-sm">
        <span className="freePrice">FREE</span> {fields.kitName}{" "}
        {fields.kitPrice
          ? `(worth £${fields.kitPrice}!) included in this course`
          : ``}
      </p>
    )
  ) : withLink && withLink.length ? (
    <p className="p-5 text-sm">
      <a
        href={`${fields.kitLink}`}
        target="_blank"
        rel="noreferrer"
        className="font-medium text-brand-600 hover:text-brand-500"
      >
        Include a {fields.kitName} for £{fields.kitPrice} at the checkout{" "}
        {fields.kitWorthPrice ? `(worth £${fields.kitWorthPrice}!)` : ``}
      </a>
    </p>
  ) : (
    <p className="p-5 text-sm">
      Include a {fields.kitName} for £{fields.kitPrice} at the checkout{" "}
      {fields.kitWorthPrice ? `(worth £${fields.kitWorthPrice}!)` : ``}
    </p>
  );

  return (
    <>
      <ul className="mt-8 space-y-5 lg:space-y-0 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:gap-y-5">
        {children}
      </ul>
      {containsKit ? (
        <div className="container">
          <div style={{ border: "1px solid rgba(234, 231, 228, 1)" }}>
            {kitText}
          </div>
        </div>
      ) : null}
    </>
  );
};

const LI = ({ node, children, fields }) => {
  return (
    <li className="flex items-start lg:col-span-1">
      <div className="shrink-0">
        <svg
          className="h-5 w-5 text-brand-400"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
            clipRule="evenodd"
          ></path>
        </svg>
      </div>
      <span className="block ml-3 text-sm text-gray-700">
        {node.content
          .map((x) => x.content.map((y) => y.value).flat())
          .join(" ")}
      </span>
    </li>
  );
};

const P = (node, children) => <p className="mt-3 text-gray-500">{children}</p>;

export default function Pricing({ primary, fields, sys, includes, event }) {
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    setTimeout(() => {
      // @ts-ignore
      window.KlarnaOnsiteService = window.KlarnaOnsiteService || [];
      // @ts-ignore
      window.KlarnaOnsiteService.push({ eventName: "refresh-placements" });
    }, 1000);
  }, []);

  const session = useSession();
  // const router = useRouter();
  const courseData = primary.fields.data;

  const options = {
    ...richContentOptions,
    renderMark: {
      ...richContentOptions.renderMark,
      [MARKS.BOLD]: Strong,
    },
    renderNode: {
      ...richContentOptions.renderNode,
      [BLOCKS.HEADING_3]: Heading3,
      [BLOCKS.HEADING_4]: Heading4,
      [BLOCKS.UL_LIST]: function ULList(node: any, children: any) {
        return (
          <UL node={node} fields={fields}>
            {children}
          </UL>
        );
      },
      [BLOCKS.LIST_ITEM]: function LiItem(node: any, children: any) {
        return (
          <LI node={node} fields={fields}>
            {children}
          </LI>
        );
      },
      [BLOCKS.PARAGRAPH]: P,
    },
  };

  const nextAvailable =
    event &&
    event.events &&
    event.events.find((x) => x.registrations.length < x.seats);

  const reduced = primary.fields.package
    ? event && event.wasPrice && event.wasPrice > 0
    : nextAvailable && nextAvailable.price < event.standardPrice;

  const first =
    event && event.events && event.events.find((x) => x.type === "onsite");

  const { packageCourses } = primary.fields;
  const columns =
    packageCourses && packageCourses.length > 0
      ? packageCourses.length > 5
        ? 4
        : packageCourses.length % 2 === 0
        ? 2
        : 3
      : 2;

  return (
    <>
      <ProductSchema {...primary} {...event} schemaType="product" />
      <div className="bg-gray-100" id="pricing">
        <div className="pt-12 sm:pt-16 lg:pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center prose lg:prose-xl mx-auto">
              <h3 className="text-2xl md:text-4xl tracking-tight font-extrabold sm:text-5xl">
                {fields.title}
              </h3>
              {fields.text && fields.text.length > 0 ? (
                <MD>{fields.text}</MD>
              ) : null}
            </div>
          </div>
        </div>
        {event && event.events && event.events.length > 0 ? (
          <CourseDates
            primary={primary}
            dates={courseData && courseData.courseDates}
            event={event}
            key={event.id}
            setSelectedDate={setSelectedDate}
          />
        ) : null}
        <div className="mt-8 bg-white pb-16 sm:mt-12 sm:pb-20 lg:pb-28">
          <div className="relative">
            <div className="absolute inset-0 h-1/2 bg-gray-100"></div>
            <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="max-w-lg md:max-w-xl mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none flex flex-col lg:flex-row lg:grid lg:grid-cols-6">
                <div className="order-1 bg-white px-6 py-8 lg:p-12 lg:col-span-4">
                  <div className="space-y-6">
                    {documentToReactComponents(fields.content, options)}
                  </div>
                  {event &&
                  event.events &&
                  event.events.length > 0 &&
                  event.events[0].seat_price < 100000 ? (
                    <div className="pt-8">
                      <KlarnaDisplay amount={event.events[0].seat_price} />
                    </div>
                  ) : null}
                </div>
                <div className="order-2 lg:order-2 lg:flex-1 py-8 px-6 text-center bg-gray-50 lg:shrink-0 lg:flex lg:flex-col lg:justify-center lg:p-12 lg:col-span-2">
                  {reduced ? (
                    <>
                      <div className="flex items-center justify-center text-1xl font-extrabold text-gray-900 tabular-nums">
                        {"Price was: "}
                      </div>
                      <div className="pb-4 flex items-center justify-center text-2xl font-extrabold text-gray-900 tabular-nums">
                        <span className="ml-2 wasPrice">
                          {new Intl.NumberFormat("en-GB", {
                            style: "currency",
                            currency: "GBP",
                          }).format(
                            event.wasPrice
                              ? event.wasPrice / 100
                              : event.standardPrice / 100
                          )}
                        </span>
                      </div>
                    </>
                  ) : null}
                  {reduced ? (
                    <div className="flex items-center justify-center text-2xl font-extrabold text-gray-900 tabular-nums">
                      {"Price today: "}
                    </div>
                  ) : null}
                  {!reduced ? (
                    <div className="flex items-center justify-center text-2xl font-extrabold text-gray-900 tabular-nums">
                      {"Total Price: "}
                    </div>
                  ) : null}
                  {nextAvailable ? (
                    <div className="flex flex-col items-center justify-center text-5xl font-extrabold text-gray-900 tabular-nums">
                      <span className="ml-2">
                        {new Intl.NumberFormat("en-GB", {
                          style: "currency",
                          currency: "GBP",
                        }).format(nextAvailable.price / 100)}
                      </span>
                      <span className="text-sm font-normal">excl VAT</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center text-5xl font-extrabold text-gray-900 tabular-nums">
                      {event && event.standardPrice && (
                        <div className="flex flex-col items-center justify-center text-5xl font-extrabold text-gray-900 tabular-nums">
                          <span className="ml-2">
                            {new Intl.NumberFormat("en-GB", {
                              style: "currency",
                              currency: "GBP",
                            }).format(event.standardPrice / 100)}
                          </span>
                          <span className="text-sm font-normal">excl VAT</span>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="mt-6">
                    <div className="">
                      <button
                        onClick={() => {
                          session.addCourse({
                            ...primary,
                            event,
                            package: event.package,
                            selectedEvent:
                              event && event.events && event.events[0]
                                ? event.events[0]
                                : null,
                          });
                          setTimeout(() => {
                            window.location.href = "/checkout";
                          }, 100);
                        }}
                        className="w-full md:w-max rounded-md bg-brand-600 px-6 py-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-500 outline outline-2 outline-offset-2 outline-brand-600 gradient-border transition-colors duration-200 hover:outline-brand-500"
                      >
                        <span className="relative z-[1]">Book Training</span>
                      </button>
                    </div>
                  </div>
                  <div className="mt-4 text-sm">
                    <p className="mb-4 text-sm">
                      <Link
                        href="/terms-and-conditions/"
                        className="font-medium text-gray-500 underline"
                      >
                        View our terms and conditions
                      </Link>
                    </p>
                    <Link
                      href="/request-a-callback/"
                      className="font-medium text-gray-900"
                    >
                      Questions?{" "}
                      <span className="font-normal text-gray-500">
                        Contact us
                      </span>
                    </Link>
                  </div>
                </div>
                {event ? (
                  <div className="flex flex-col items-center justify-center space-y-6 text-xl font-extrabold text-gray-900 tabular-nums w-full order-3 pb-6 bg-gray-50 lg:row-start-2 lg:col-span-12 lg:flex-row lg:space-y-0 lg:pt-4 lg:space-x-6 lg:bg-white">
                    <div id="snap-uk-widgets-product-rbp"></div>
                    <div id="snap-uk-widgets-product-bnpl"></div>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
      {packageCourses && packageCourses.length > 0 && (
        <div
          id="package-courses"
          className="mt-8 bg-white pb-16 sm:mt-12 sm:pb-20 lg:pb-16"
        >
          <div className="w-full relative">
            <div className="absolute inset-0 h-1/2 bg-gray-100 -top-20"></div>
            <div className="w-full relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="w-full max-w-lg mx-auto rounded-lg shadow-lg overflow-hidden lg:max-w-none lg:flex">
                <div className="bg-white relative px-6 py-8 lg:p-12 w-full">
                  <div className="text-center">
                    <h2 className="mb-12 text-3xl tracking-tight font-sans font-semibold text-gray-900 sm:text-4xl">
                      Courses Included In This Training Package
                    </h2>
                  </div>
                  <div
                    className={`md:mt-12 relative mx-auto grid gap-12 md:grid-cols-${
                      columns - 1
                    } lg:grid-cols-${columns} lg:max-w-none`}
                  >
                    {packageCourses &&
                      packageCourses.sort(com).map((item, key) => {
                        const Cmp = ComponentMap[item.sys.contentType.sys.id];
                        if (Cmp) {
                          return (
                            <Cmp
                              key={`${key}-${item.sys.contentType.sys.id}`}
                              fields={item.fields}
                              sys={item.sys}
                              includes={includes}
                              suppress
                              // addItem={addItem}
                              // removeItem={removeItem}
                            />
                          );
                        }
                        return (
                          <ComponentMap.card
                            key={`${key}-${item.sys.contentType.sys.id}`}
                            fields={item.fields}
                            sys={item.sys}
                            options={{}}
                            suppress
                            includes={includes}
                          />
                        );
                      })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
